<template>
  <v-app style="background-color: white">
    <NavBar v-if="!isClient" />
    <ClientNavBar :name="envName" :color="envColor" v-if="isClient" />
    <v-content :style="{
      'background-color': isClient ? undefined : '#F7FAFE'
    }">
      <MaintenancePage v-if="maintenance"/>
      <router-view v-else></router-view>
    </v-content>
    <Footer v-if="!isClient" :class="{'with-sidebar': showSideBar}"/>
    <v-snackbar
      v-model="showSystemMessage"
      :timeout=10000
      :color="systemMessageColor"
    >
      <span :class="{headline: isWarningMessage}">{{systemMessage}}</span>
    </v-snackbar>
    <Loading :show="openLoadingOverlay" :message="loadingMessage" />
  </v-app>
</template>

<script>
import Footer from "./components/organisms/Footer";
import NavBar from "./components/organisms/NavBar";
import ClientNavBar from "./components/organisms/ClientNavBar";
import {mapActions, mapGetters} from "vuex";
import ApiMixin from "./ApiMixin";
import Loading from "./components/molecules/Loading";
import ComponentMixin from "./components/ComponentMixin";
import MaintenancePage from "@/components/pages/common/MaintenancePage";

export default {
  mixins: [ComponentMixin, ApiMixin],
  name: 'App',
  type: "app",
  components: {
    MaintenancePage,
    NavBar,
    ClientNavBar,
    Footer,
    Loading,
  },
  computed: {
    ...mapGetters("system", ["message", "isRequesting", "showSideBar"]),
    ...mapGetters("user", ["user"]),
    showSystemMessage: {
      get () {
        return this.message !== null;
      },
      set () {
        this.clearMessage();
      }
    },
    notLogin() {
      return !this.$auth.loading && !this.$auth.isAuthenticated
    },
    systemMessage () {
      if (this.message) {
        return this.message.label;
      }
      return null;
    },
    systemMessageColor () {
      if (this.message) {
        return this.message.type;
      }
      return null;
    },
    envName () {
      if (process.env.NODE_ENV === "production") {
        return "";
      }
      return process.env.NODE_ENV;
    },
    envColor () {
      if (process.env.NODE_ENV === "production") {
        return "primary";
      }
      return "#228B22";
    },
    isClient () {
      if (this.$route.path.indexOf('/clients') == 0) {
        return true;
      }
      return false;
    },
    maintenance () {
      return process.env.VUE_APP_MAINTE === "true"
    },
    isWarningMessage() {
      return Boolean(this.message && this.message.type === 'warning')
    },
    loadingMessage() {
      if (this.$auth.loading) return 'ログインを確認しています'
      if (this.isRequesting) return 'データを更新中です'
      return null
    },
    /** スカウト中に加え、ログイン未確認時にローディング表示を追加 */
    openLoadingOverlay() {
      if (this.notLogin) return false
      return this.isRequesting || this.$auth.loading
    }
  },
  methods: {
    ...mapActions("system", ["clearMessage", "showWarning", "showError"]),
    validateRequiredLogin (to) {
      if (to.meta && to.meta.requiredLogin && (!this.$auth || !this.$auth.isAuthenticated)) {
        localStorage.setItem('nishika_redirect', window.location.href)
        this.$auth.loginWithRedirect({ui_locales: this.$i18n.locale})
      }
    },
    onError (el) {
      const dialog = document.getElementsByClassName('profileEditDialog')[0];
      dialog.scrollTop = el.offsetTop;
    },
    isSignUp(user){
      return user.gender === null
    },
    isFirstAccess(user){
      return user.favoriteServices === null
    },
  },
  beforeMount () {
    const lang = localStorage.getItem('locale');
    this.loadConsts(lang);
    this.dispatch("temporary", "load")
  },
  watch: {
    "$auth.isAuthenticated": {
      handler: async function(val) {
        if (val) {
          const res = await this.loadUser(this.isClient)
          const user = res.data
          if (this.isClient) {
            this.gtmDataLayer({
              event: "login",
              service: "connect-clients",
              user_id: user.uuid
            })
          } else {
            this.gtmDataLayer({
              event: "login",
              service: "connect",
              user_id: user.uuid
            })

            if ( this.isSignUp(user) || this.isFirstAccess(user)) {
              window.location.href = this.toProfile('/dashboard')
              return
            }
            this.loadUserMessages(user.id)
            this.loadNotifications(user.id)
            this.loadUnprocessedOffers(user.id)
          }
        }
      }
    },
    "$auth.loading": {
      handler: function(loading) {
        if (!loading) {
          this.validateRequiredLogin(this.$route);
        }
      }
    },
    "$auth.user": {
      handler: function(val) {
        if (val && !val.email_verified) {
          this.showWarning({message: this.label("notEmailVerified")});
        }
      }
    },
    "$auth.error": {
      handler: function(e) {
        if (e) {
          if (e.error_description === 'Please verify your email before logging in.') {
            this.showWarning({message: this.label("notEmailVerified")});
          }
        }
      }
    },
    "$route": "validateRequiredLogin",
    "$route.path": {
      // DS側はリポジトリ移動したのでリダイレクトする
      handler(path) {
        const forDS = !path.startsWith('/clients')
        if (forDS) {
          window.location.href = `${process.env.VUE_APP_NISHIKA_URL}/jobs`
        }
      },
      immediate: true,
    }
  }
};
</script>

<style lang="sass">
.v-content
  margin-top: 64px

.v-application
  font-family: 'メイリオ', Meiryo, 'Segoe UI', sans-serif !important
  -webkit-font-smoothing: antialiased

a
  text-decoration: none
  background-color: transparent

.with-sidebar
  margin-left: 280px

// 中央寄せ(松田さんの要望)
.v-snack__wrapper
  min-width: 0 !important
</style>
